import React, { useState, useEffect, useCallback, useRef } from 'react';
import $i18n from '@/i18n';
import Confirm from '@/components/Confirm';
import { Table, Icon, Message } from '@b-design/fusion';
import {  Divider, Input } from '@alifd/next';
import Button from '@/components/Button';
import { urlEmun, miniAppMemberColumns, renderActionItem } from './const.js';
import CalculateComponent from '@/components/CalculateComponent';
import PaginationComponent from '@/components/PaginationComponent';
import SetAsModal from '@/components/SetAsModal';
import AddMemberModal from '../addMemberModal';
import { getQueryStringArgs } from '@/utils/url-parse';
import Requests from '@/services/request';
import store from '@/store';

import './index.less';

interface Member {
  appRole: string[];
}

const Member: React.FC<Member> = ({ appRole }) => {
  const [userState] = store.useModel('user');

  // table数据
  const [dataSource, setDataSource] = useState({ list: [], total: 0 });
  // 内容区域高度
  const [contentHeight, setContentHeight] = useState(200);
  const [column, setcolumns] = useState(miniAppMemberColumns);
  const [loading, setLoading] = useState(false);

  // 增加成员弹框
  const [addMemberVisible, setAddMemberVisible] = useState<boolean>(false);

  // 分页参数
  const [paginationValue, setPaginationValue] = useState({
    currentPage: 1,
    pageSize: 10,
  });
  // 搜索value
  // const [searchValue, seSearchValue] = useState<string | undefined>();
  // filter
  const [filterValue, setFilterValue] = useState<{
    role?: string | undefined;
    status?: string | undefined;
  }>();

  // Set As弹框
  const [setAsVisible, setSetAsVisible] = useState<boolean>(false);
  // Set As角色选择
  const [setAsRole, setSetAsRole] = useState<string[]>([]);

  const { appId, spaceId } = getQueryStringArgs();
  
  // 修改角色时
  const memberInfo = useRef<Record<'accountIdList' | 'authId', string[] | string>>({
    accountIdList: [],
    authId: '',
  });

  const onOpen = () => {
    setSetAsVisible(true);
  };

  const onClose = () => {
    setSetAsVisible(false);
  };

  // 获取应用成员
  const queryChannel = useCallback(
    (params: any = { authId: appId, currentPage: 1, pageSize: 10 }) => {
      setLoading(true);
      Requests.get('/rest/intercnn/app/auth/query', { ...params }).then((data) => {
        if (data) {
          const { list = [], total } = data;
          setDataSource({ list, total });
          setLoading(false);
        }
      });
    },
    [appId],
  );

  useEffect(() => {
    queryChannel({ authId: appId, ...paginationValue, ...filterValue });
  }, [paginationValue, filterValue]);

  const onOk = useCallback(
    async (type, params = {}) => {
      const data = await Requests.post(
        urlEmun?.[type],
        type === 'add' ? { ...memberInfo?.current, role: setAsRole?.[0] } : { ...params },
      );
      if (data) {
        type === 'add'
          ? Message.success({
              title: $i18n.get({
                id: 'aopf.pages.channel.channelList.message.success',
                dm: '修改成功',
              }),
            })
          : Message.success({
              title: $i18n.get({
                id: 'aopf.pages.invitation.spaces.message.deleteSuccess',
                dm: '删除成功',
              }),
            });
        onClose();
        queryChannel({ authId: appId, ...paginationValue, ...filterValue });
      }
    },
    [memberInfo?.current, setAsRole, onClose, queryChannel, filterValue],
  );

  // 根据用户角色处理column
  useEffect(() => {
    const actionRender = {
      title: $i18n.get({ id: 'aopf.pages.member.columnsAction', dm: '操作' }),
      dataIndex: 'action',
      width: 180,
      cell: (_v, _i, record) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            fontSize: '12px',
            color: 'var(--superapp-primary-1)',
          }}
        >
          {userState?.currentUser?.accountId === record?.accountId ? (
            renderActionItem({
              icon: 'settings--outline',
              content: $i18n.get({
                id: 'aopf.pages.member.columnsActionSetAs',
                dm: '设置',
              }),
              disable: true,
            })
          ) : (
            <div
              onClick={() => {
                onOpen();
                setSetAsRole(record?.role ? [record?.role] : []);
                memberInfo.current = { accountIdList: [record?.accountId], authId: record?.authId };
              }}
            >
              {renderActionItem({
                icon: 'settings--outline',
                content: $i18n.get({
                  id: 'aopf.pages.member.columnsActionSetAs',
                  dm: '设置',
                }),
              })}
            </div>
          )}

          <Divider direction="ver" />

          {record?.status === 'BLOCKED' ||
          userState?.currentUser?.accountId === record?.accountId ? (
            renderActionItem({
              icon: 'trash-can--outline',
              content: $i18n.get({
                id: 'aopf.pages.member.columnsActionDelete',
                dm: '删除',
              }),
              disable: true,
            })
          ) : (
            <Confirm
              trigger={renderActionItem({
                icon: 'trash-can--outline',
                content: $i18n.get({
                  id: 'aopf.pages.member.columnsActionDelete',
                  dm: '删除',
                }),
              })}
              dialogTitle={$i18n.get({
                id: 'aopf.pages.member.actionDeletePop.title',
                dm: '确定删除此成员?',
              })}
              dialogContent={$i18n.get({
                id: 'aopf.pages.member.actionDeletePop',
                dm: '确定删除此成员?',
              })}
              onClick={onOk.bind(null, 'delete', { accountId: record?.accountId, authId: record?.authId })}
              width={480}
            />
          )}
        </div>
      ),
    };
    if (appRole?.includes('ADMIN')) {
      setcolumns([...miniAppMemberColumns, actionRender]);
    } else {
      setcolumns([...miniAppMemberColumns]);
    }
  }, [appRole, onOk]);


  return (
    <div className="global-filter-list">
      <div className="list-content">
        <div className="content-btns">
          <div className="btns-left">
            {appRole?.includes('ADMIN') && (
              <Button
                type="primary"
                data-child-type="icon-text"
                id="btn-create-miniapp"
                onClick={() => setAddMemberVisible(true)}
              >
                <Icon type="add--circle--filled" />
                {$i18n.get({ id: 'aopf.pages.member.addMembers', dm: '添加成员' })}
              </Button>
            )}
          </div>
        </div>
        <Table.StickyLock
          columns={column}
          fixedHeader
          maxBodyHeight={contentHeight - 54}
          dataSource={dataSource?.list}
          primaryKey="accountId"
          loading={loading}
          onFilter={(filterParams) => {
            const filter: any = {};
            for (const [key, value] of Object.entries(filterParams)) {
              filter[key] = (value as any)?.selectedKeys?.[0];
            }
            setFilterValue((pre) => ({ ...pre, ...filter }));
          }}
        />

        <PaginationComponent
          total={dataSource?.total}
          current={paginationValue?.currentPage}
          pageSize={paginationValue?.pageSize}
          onPageChange={(current, pageSize) => {
            setPaginationValue({
              currentPage: current,
              pageSize,
            });
          }}
        />

        {/* </Loading> */}
      </div>

      <CalculateComponent
        otherWidth={280}
        otherHeight={88 + 48 + 50 + 56 + 56}
        windowChange={(_width, height) => {
          setContentHeight(height);
        }}
      />

      {/* 设置弹框 */}
      <SetAsModal
        setAsVisible={setAsVisible}
        setSetAsRole={setSetAsRole}
        setAsRole={setAsRole}
        onClose={onClose}
        onOk={onOk.bind(null, 'add')}
        type="app"
      />

      {/* 增加成员 */}
      {appRole?.includes('ADMIN') && (
        <AddMemberModal
          addMemberVisible={addMemberVisible}
          setAddMemberVisible={setAddMemberVisible}
          spaceId={spaceId || ''}
          appId={appId}
          queryChannel={() => {
            queryChannel({ authId: appId, ...paginationValue });
          }}
        />
      )}
    </div>
  );
};

export default Member;
